import { Dropdown } from '@platform/ui-library';
import {
  FC,
  ReactElement,
  useEffect
} from 'react';
import { useDesign } from '../../hooks';
import { useGlobalSettings } from '../../stores';
import { Design, LanguageCode, Voice } from '../../types';
import { getLanguageLabel } from '../../utilities';

type LanguageSelectorProps = {
  onChange?: () => void;
};

export const LanguageSelector: FC<LanguageSelectorProps> = ({ onChange }) => {
  const serialized = useDesign();
  const { language, setSettings } = useGlobalSettings();
  const defaultLanguage = LanguageCode.englishUs;
  const languages = getLanguages(serialized.languages);

  useEffect(() => {
    if (!language || !(language in languages)) {
      handleChange(defaultLanguage);
    }
  }, [language, languages, setSettings]);

  const handleChange = (language: string) => {
    const voice = getVoice(language as LanguageCode) ?? Voice.phil;
    setSettings({
      language: language as LanguageCode,
      voice
    });
    window.apm.postEventToCurrentView({ target: 'request-refresh' });
    if (onChange) {
      onChange();
    }
  };

  return (
    <Dropdown
      options={languages}
      onChange={handleChange}
      value={language ?? defaultLanguage ?? ''}
      className="selector"
    />
  );
};

function getLanguages(languages: Design['languages']): Record<string, string | ReactElement> {
  return Object.fromEntries(
    languages.map((language) => {
      const { language: languageCode } = language;
      const count = languages.filter(({ language }) => languageCode === language).length;
      return [languageCode, <>{getLanguageLabel({
        designLanguage: language,
        includeTts: count < 2
      })}</>];
    })
  );
}

function getVoice(languageCode: LanguageCode) {
  const { serialized } = window.apm;
  return (serialized.voice = serialized.languages.find(({ language }) => {
    return language === languageCode;
  })?.voice as Voice);
}
